import React, { useState, useEffect } from 'react';
import CommonDataTable from '../../Common/CommonDataTable';
import LoadingSpinner from '../../Common/LoadingSpinner';

/**
 * Advance / on-account payments of the supplier set off in an Against GRN payment: the set-off is
 * part of the GRN amount already paid, so only the rest is paid out now.
 */
const AdvanceAllocationGrid = ({
    supplier,
    advanceAllocations = [],
    onAdvanceAllocationsChange,
    errors = {},
    loading = false,
    onLoadAvailableAdvances
}) => {
    const [availableAdvances, setAvailableAdvances] = useState([]);
    const [loadingAdvances, setLoadingAdvances] = useState(false);
    const [showAdd, setShowAdd] = useState(false);

    useEffect(() => {
        if (supplier && onLoadAvailableAdvances) loadAvailableAdvances();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplier]);

    const loadAvailableAdvances = async () => {
        setLoadingAdvances(true);
        try {
            setAvailableAdvances(await onLoadAvailableAdvances(supplier));
        } catch (error) {
            console.error('Error loading supplier advances:', error);
        } finally {
            setLoadingAdvances(false);
        }
    };

    const handleAmountChange = (index, value) => {
        const updated = [...advanceAllocations];
        const amount = parseFloat(value) || 0;
        updated[index] = { ...updated[index], amountSetoff: amount, remainingBalance: (updated[index].setoffAmount || 0) - amount };
        onAdvanceAllocationsChange(updated);
    };

    const handleNotesChange = (index, notes) => {
        const updated = [...advanceAllocations];
        updated[index] = { ...updated[index], notes };
        onAdvanceAllocationsChange(updated);
    };

    const handleAdd = (adv) => {
        const open = adv.advance?.remainingAmount || 0;
        onAdvanceAllocationsChange([...advanceAllocations, {
            advancePayment: adv._id,
            advanceNo: adv.paymentVoucherNo,
            advanceType: adv.paymentType,
            advanceDate: adv.paymentDate,
            purchaseOrderNumber: adv.advance?.purchaseOrderNumber || '',
            advanceAmount: adv.advance?.amount || 0,
            setoffAmount: open,
            amountSetoff: 0,
            remainingBalance: open,
            notes: ''
        }]);
        setShowAdd(false);
    };

    const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : '-');
    const typeLabel = (t) => (t === 'on_account' ? 'On account' : 'Advance');
    const money = (v) => parseFloat(v || 0).toFixed(2);

    const columns = [
        { key: 'advanceNo', header: 'Payment No.', width: '150px' },
        { key: 'advanceType', header: 'Type', width: '100px', render: (value) => typeLabel(value) },
        { key: 'advanceDate', header: 'Date', width: '100px', render: (value) => formatDate(value) },
        { key: 'purchaseOrderNumber', header: 'PO', width: '120px', render: (value) => value || '—' },
        { key: 'setoffAmount', header: 'Open', width: '120px', render: (value) => money(value) },
        {
            key: 'amountSetoff',
            header: 'Amount Setoff',
            width: '140px',
            render: (value, row, index) => (
                <input
                    type="number"
                    value={value === 0 ? '0' : (value || '')}
                    onChange={(e) => handleAmountChange(index, e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    max={row.setoffAmount}
                    step="0.01"
                />
            )
        },
        {
            key: 'remainingBalance',
            header: 'Left Open',
            width: '120px',
            render: (value) => <span className={`font-medium ${value > 0 ? 'text-orange-600' : 'text-green-600'}`}>{money(value)}</span>
        },
        {
            key: 'notes',
            header: 'Notes',
            render: (value, row, index) => (
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => handleNotesChange(index, e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add notes..."
                />
            )
        }
    ];

    const availableColumns = [
        { key: 'paymentVoucherNo', header: 'Payment No.', width: '150px' },
        { key: 'paymentType', header: 'Type', width: '100px', render: (value) => typeLabel(value) },
        { key: 'paymentDate', header: 'Date', width: '100px', render: (value) => formatDate(value) },
        { header: 'PO', width: '120px', render: (value, row) => row?.advance?.purchaseOrderNumber || '—' },
        { header: 'Paid', width: '120px', render: (value, row) => money(row?.advance?.amount) },
        { header: 'Open', width: '120px', render: (value, row) => money(row?.advance?.remainingAmount) }
    ];

    const filteredAvailable = availableAdvances.filter(adv => !advanceAllocations.some(a => String(a.advancePayment?._id || a.advancePayment) === String(adv._id)));
    const total = advanceAllocations.reduce((sum, a) => sum + (a.amountSetoff || 0), 0);

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between bg-gradient-to-r from-teal-600 to-emerald-600 px-3 py-1.5">
                <span className="text-xs font-semibold text-white">Advance / On-account Set-off</span>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-teal-100">Total: {money(total)}</span>
                    {supplier && (
                        <button
                            type="button"
                            onClick={() => setShowAdd(true)}
                            className="flex items-center gap-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs text-white transition-colors"
                            disabled={loadingAdvances}
                        >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Advance
                        </button>
                    )}
                </div>
            </div>
            <div className="p-3">
                {errors.advanceAllocations && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{errors.advanceAllocations}</p>
                    </div>
                )}
                {advanceAllocations.length === 0 ? (
                    <p className="text-center py-3 text-sm text-gray-500">
                        {loadingAdvances ? 'Loading advances…'
                            : availableAdvances.length
                                ? `${availableAdvances.length} advance / on-account payment(s) open — ${money(availableAdvances.reduce((s, a) => s + (a.advance?.remainingAmount || 0), 0))}. Click "Add Advance" to set one off.`
                                : 'No open advances or on-account payments for this supplier'}
                    </p>
                ) : (
                    <CommonDataTable
                        data={advanceAllocations}
                        columns={columns}
                        loading={loading}
                        emptyMessage="No advance set-offs"
                        customActions={[{
                            label: 'Remove',
                            onClick: (row, index) => onAdvanceAllocationsChange(advanceAllocations.filter((_, i) => i !== index)),
                            icon: (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            ),
                            color: 'text-red-600 hover:text-red-800'
                        }]}
                    />
                )}

                {showAdd && (
                    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                        <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-emerald-600 rounded-t-xl px-6 py-4 z-10 flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-white">Open Advances / On-account Payments</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowAdd(false)}
                                    className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="p-6">
                                {loadingAdvances ? (
                                    <div className="flex items-center justify-center py-8"><LoadingSpinner /><span className="ml-2 text-gray-600">Loading advances...</span></div>
                                ) : filteredAvailable.length === 0 ? (
                                    <p className="text-center py-8 text-gray-500">No open advances left for this supplier</p>
                                ) : (
                                    <CommonDataTable
                                        data={filteredAvailable}
                                        columns={availableColumns}
                                        emptyMessage="No open advances"
                                        onRowClick={handleAdd}
                                        customActions={[{
                                            label: 'Add',
                                            onClick: handleAdd,
                                            icon: (
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                </svg>
                                            ),
                                            color: 'text-green-600 hover:text-green-800'
                                        }]}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdvanceAllocationGrid;
